import { type ThemeOptions } from "@mui/material/styles";

export const FONT_FAMILY = "'Figtree', sans-serif";

/**
 * A literal snapshot of Tailwind v3's `fontSize` map (`md` is Tailwind's `base`). Literal on purpose:
 * the theme owns its rendered pixels, so a Tailwind upgrade cannot move them silently.
 */
export const TYPE_SCALE = {
  xs: { fontSize: "0.75rem", lineHeight: "1rem" },
  sm: { fontSize: "0.875rem", lineHeight: "1.25rem" },
  md: { fontSize: "1rem", lineHeight: "1.5rem" },
  lg: { fontSize: "1.125rem", lineHeight: "1.75rem" },
  xl: { fontSize: "1.25rem", lineHeight: "1.75rem" },
  "2xl": { fontSize: "1.5rem", lineHeight: "2rem" },
  "3xl": { fontSize: "1.875rem", lineHeight: "2.25rem" },
} as const;

/**
 * `global.css`'s document line-height. Every type variant carries an absolute line-height instead, so
 * this is left to the two places that still inherit the document's: `overline`, which has no token of
 * its own, and `MuiTableCell`, whose rows inherit their weight from the group above them.
 */
export const BODY_LINE_HEIGHT = 1.4;

/** The eyebrow treatment — `overline`'s alone, since `h5` became a plain heading. */
const EYEBROW_LETTER_SPACING = "2px";

/** MUI's `typography` has no monospace slot. No consumer today; exported so the token is not lost. */
export const FONT_FAMILY_MONO = `SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace`;

/**
 * MUI's `typography` has one `button` slot for three sizes, so the per-size labels are their own
 * theme entries (`src/types/mui.d.ts`); `components/button.ts` applies them.
 */
export const BUTTON_TYPE = {
  large: { ...TYPE_SCALE.md, fontWeight: 700 },
  medium: { ...TYPE_SCALE.sm, fontWeight: 700 },
  small: { ...TYPE_SCALE.xs, fontWeight: 600 },
} as const;

export const typography: ThemeOptions["typography"] = {
  fontFamily: FONT_FAMILY,

  h1: {
    ...TYPE_SCALE["3xl"],
    fontWeight: 700,
  },
  h2: {
    ...TYPE_SCALE["2xl"],
    fontWeight: 700,
  },
  h3: {
    ...TYPE_SCALE.xl,
    fontWeight: 700,
  },
  h4: {
    ...TYPE_SCALE.lg,
    fontWeight: 700,
  },
  h5: {
    ...TYPE_SCALE.md,
    fontWeight: 700,
  },
  h6: {
    ...TYPE_SCALE.sm,
    fontWeight: 700,
  },

  subtitle1: {
    ...TYPE_SCALE.md,
    fontWeight: 600,
  },
  subtitle2: {
    ...TYPE_SCALE.sm,
    fontWeight: 600,
  },
  body1: {
    ...TYPE_SCALE.md,
    fontWeight: 400,
  },
  body2: {
    ...TYPE_SCALE.sm,
    fontWeight: 400,
  },
  caption: {
    ...TYPE_SCALE.xs,
    fontWeight: 500,
  },
  overline: {
    fontSize: TYPE_SCALE.xs.fontSize,
    lineHeight: BODY_LINE_HEIGHT,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: EYEBROW_LETTER_SPACING,
  },

  buttonLarge: BUTTON_TYPE.large,
  buttonMedium: BUTTON_TYPE.medium,
  buttonSmall: BUTTON_TYPE.small,

  /**
   * Shared by `Button`, `Fab`, `Tab` and `ToggleButton`, which is why the button's own per-size
   * metrics live in `components/button.ts` instead of here.
   */
  button: {
    textTransform: "none",
    fontWeight: 600,
  },
};
